<template>
  <div class="image-loader" :style="containerStyle">
    <!-- Skeleton Loader -->
    <div
        v-if="isLoading || loading"
        class="skeleton-placeholder"
        :style="{
        borderRadius: `${shimmerRadius}px`
      }"
    >
      <div class="skeleton-animation"></div>
    </div>

    <!-- Image -->
    <img
        v-if="source"
        :src="source"
        :style="imageStyle"
        @load="handleImageLoad"
        @error="handleImageError"
        class="image"
        alt=""
    />
  </div>
</template>

<script setup lang="ts">
import { ref, computed, CSSProperties } from 'vue'

interface Props {
  source?: string
  style?: CSSProperties
  shimmerRadius?: number
  loading?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  shimmerRadius: 8,
  loading: false
})

const isLoading = ref(true)

const containerStyle = computed(() => ({
  ...props.style,
  position: 'relative' as const
}))

const imageStyle = computed(() => ({
  width: '100%',
  height: '100%',
  display: isLoading.value || props.loading ? 'none' : 'block'
}))

const handleImageLoad = () => {
  isLoading.value = false
}

const handleImageError = () => {
  isLoading.value = false
}
</script>

<style scoped>
.image-loader {
  display: block;
  overflow: hidden;
}

.skeleton-placeholder {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: #e0e0e0;
  overflow: hidden;
}

.skeleton-animation {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(
      90deg,
      rgba(255, 255, 255, 0) 0%,
      rgba(255, 255, 255, 0.5) 50%,
      rgba(255, 255, 255, 0) 100%
  );
  animation: shimmer 1.5s infinite;
}

@keyframes shimmer {
  0% {
    transform: translateX(-100%);
  }
  100% {
    transform: translateX(100%);
  }
}

.image {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
</style>